/**
 * The relay's invite answers as the writer's device reads them (part 38b; part 38a's routes, table IS): each read field by
 * field at the boundary (Definition of Done, security), anything else null, which the client counts a failure (IW13).
 * What the device sends is built here too, so every body leaves from one place and the request inspection (IW12) has one
 * place to hold to: the proofs, never the secrets; never a person key.
 */
import type { InviteNotes, InviteRole, KeyGroup } from '@cockatrice/storage';
import { INVITE_NOTES, INVITE_ROLES, KEY_GROUPS } from '@cockatrice/storage';
import { bytesOfLength, fromBase64Url, toBase64Url } from '../account/bytes.ts';
import { fieldsOf } from '../account/wire.ts';

/** An invite's state as the relay judges it (part 38a's `InviteState`). */
export type InviteState = 'pending' | 'claimed' | 'removed' | 'expired' | 'lost' | 'share-ended';

const INVITE_STATES: readonly InviteState[] = ['pending', 'claimed', 'removed', 'expired', 'lost', 'share-ended'];

/** Whether an invite in `state` is still open: changed, pushed to and (claimed) served (part 38a's `isOpen`). */
export function isOpen(state: InviteState): boolean {
  return state === 'pending' || state === 'claimed';
}

/** A share's and an invite's ID: 16 bytes, base64url (part 38a's `SHARE_ID_BYTES`). */
export const INVITE_WIRE_ID_BYTES = 16;

/** The settings an invite holds at the relay (part 38a's `InviteSettings`); the chapters in code-unit order. */
export interface InviteSettings {
  readonly role: InviteRole;
  readonly notes: InviteNotes;
  readonly export: boolean;
  readonly endsAt: number | null;
  readonly chapters: readonly string[];
}

/** Table IR: `settings` in the one spelling the record keeps as what the relay took (`InviteRecord.sent`). */
export function settingsKey(settings: InviteSettings): string {
  return JSON.stringify({ role: settings.role, notes: settings.notes, export: settings.export, endsAt: settings.endsAt, chapters: [...settings.chapters].sort() });
}

function timeOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/** A time or null; undefined when it is neither. */
function timeOrNull(value: unknown): number | null | undefined {
  return value === null ? null : (timeOf(value) ?? undefined);
}

/** An ID of {@link INVITE_WIRE_ID_BYTES} bytes, base64url; null otherwise. */
function idOf(value: unknown): string | null {
  return typeof value === 'string' && bytesOfLength(value, INVITE_WIRE_ID_BYTES) !== null ? value : null;
}

function stateOf(value: unknown): InviteState | null {
  return INVITE_STATES.find((state) => state === value) ?? null;
}

function roleOf(value: unknown): InviteRole | null {
  return INVITE_ROLES.find((role) => role === value) ?? null;
}

function notesOf(value: unknown): InviteNotes | null {
  return INVITE_NOTES.find((notes) => notes === value) ?? null;
}

/** `{ ok: true }`, or null. */
export function readOk(value: unknown): true | null {
  return fieldsOf(value)?.['ok'] === true ? true : null;
}

/** An invite as the owner's list shows it (part 38a's `OwnerInvite`). */
export interface OwnerInvite {
  readonly id: string;
  readonly email: string;
  readonly role: InviteRole;
  readonly notes: InviteNotes;
  readonly export: boolean;
  readonly endsAt: number | null;
  readonly chapters: readonly string[];
  readonly createdAt: number;
  readonly claimedAt: number | null;
  readonly state: InviteState;
  readonly noteCount: number;
}

/** The owner's list of a share (part 38a's `listInvites`): its end and its invites. */
export interface OwnerList {
  readonly endedAt: number | null;
  readonly invites: readonly OwnerInvite[];
}

function ownerInviteOf(value: unknown): OwnerInvite | null {
  const fields = fieldsOf(value);
  const id = idOf(fields?.['id']);
  const email = fields?.['email'];
  const role = roleOf(fields?.['role']);
  const notes = notesOf(fields?.['notes']);
  const exported = fields?.['export'];
  const endsAt = timeOrNull(fields?.['endsAt']);
  const raw = fields?.['chapters'];
  const createdAt = timeOf(fields?.['createdAt']);
  const claimedAt = timeOrNull(fields?.['claimedAt']);
  const state = stateOf(fields?.['state']);
  const noteCount = timeOf(fields?.['noteCount']);
  if (id === null || typeof email !== 'string' || role === null || notes === null || typeof exported !== 'boolean' || endsAt === undefined || !Array.isArray(raw)) return null;
  if (createdAt === null || claimedAt === undefined || state === null || noteCount === null) return null;
  const chapters = raw.filter((chapter): chapter is string => typeof chapter === 'string');
  if (chapters.length !== raw.length) return null;
  return { id, email, role, notes, export: exported, endsAt, chapters, createdAt, claimedAt, state, noteCount };
}

/** `POST /v1/shares/invites`: `{ endedAt, invites }`, or null. */
export function readOwnerList(value: unknown): OwnerList | null {
  const fields = fieldsOf(value);
  const endedAt = timeOrNull(fields?.['endedAt']);
  const raw = fields?.['invites'];
  if (endedAt === undefined || !Array.isArray(raw)) return null;
  const invites = raw.map(ownerInviteOf);
  return invites.every((invite): invite is OwnerInvite => invite !== null) ? { endedAt, invites } : null;
}

/** An invite as the account's session list shows it (part 38a's `ListedInvite`: no hash, no chapter). */
export interface ListedInvite {
  readonly id: string;
  readonly email: string;
  readonly role: InviteRole;
  readonly state: InviteState;
}

/** A share as the account's session list shows it (part 38a's `ListedShare`). */
export interface ListedShare {
  readonly id: string;
  readonly endedAt: number | null;
  readonly invites: readonly ListedInvite[];
}

function listedInviteOf(value: unknown): ListedInvite | null {
  const fields = fieldsOf(value);
  const id = idOf(fields?.['id']);
  const email = fields?.['email'];
  const role = roleOf(fields?.['role']);
  const state = stateOf(fields?.['state']);
  return id === null || typeof email !== 'string' || role === null || state === null ? null : { id, email, role, state };
}

function listedShareOf(value: unknown): ListedShare | null {
  const fields = fieldsOf(value);
  const id = idOf(fields?.['id']);
  const endedAt = timeOrNull(fields?.['endedAt']);
  const raw = fields?.['invites'];
  if (id === null || endedAt === undefined || !Array.isArray(raw)) return null;
  const invites = raw.map(listedInviteOf);
  return invites.every((invite): invite is ListedInvite => invite !== null) ? { id, endedAt, invites } : null;
}

/** `GET /v1/shares`: `{ shares }`, or null. */
export function readShareList(value: unknown): readonly ListedShare[] | null {
  const raw = fieldsOf(value)?.['shares'];
  if (!Array.isArray(raw)) return null;
  const shares = raw.map(listedShareOf);
  return shares.every((share): share is ListedShare => share !== null) ? shares : null;
}

/** `POST /v1/shares/end-all`: `{ ended }`, or null. */
export function readEndedAll(value: unknown): number | null {
  return timeOf(fieldsOf(value)?.['ended']);
}

/** IW12: an owner's body for share `share` with proof `owner`: what the relay is sent of the secret is the proof alone. */
export function shareBody(share: string, owner: Uint8Array, more: Readonly<Record<string, unknown>> = {}): Record<string, unknown> {
  return { share, owner: toBase64Url(owner), ...more };
}

/** The settings as the relay's bodies spell them. */
function settingsFields(settings: InviteSettings): Record<string, unknown> {
  return { role: settings.role, notes: settings.notes, export: settings.export, endsAt: settings.endsAt, chapters: [...settings.chapters].sort() };
}

/** `POST /v1/shares/invite`'s body: the invite's ID, its claim proof (never the claim secret), the address and the settings. */
export function inviteBody(share: string, owner: Uint8Array, invite: string, claim: Uint8Array, email: string, settings: InviteSettings): Record<string, unknown> {
  return shareBody(share, owner, { invite, claim: toBase64Url(claim), email, ...settingsFields(settings) });
}

/** `POST /v1/shares/invite/change`'s body: every setting, replaced. */
export function changeBody(share: string, owner: Uint8Array, invite: string, settings: InviteSettings): Record<string, unknown> {
  return shareBody(share, owner, { invite, ...settingsFields(settings) });
}

/** `POST /v1/shares/copy`'s frame (part 38a's `copyFrame`): four bytes giving the JSON header's length, the header, the copy. */
export function copyFrame(share: string, owner: Uint8Array, item: string, version: Uint8Array, wraps: ReadonlyMap<string, Uint8Array>, bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const header = new TextEncoder().encode(
    JSON.stringify(shareBody(share, owner, { chapter: item, version: toBase64Url(version), wraps: Object.fromEntries([...wraps].map(([invite, wrap]) => [invite, toBase64Url(wrap)])) })),
  );
  const frame = new Uint8Array(4 + header.length + bytes.length);
  new DataView(frame.buffer).setUint32(0, header.length);
  frame.set(header, 4);
  frame.set(bytes, 4 + header.length);
  return frame;
}

/** Part 38e: a group a note sits in as the relay names it (part 38a's `NoteGroup`): a key group, or one invitee's thread. */
export type WireNoteGroup = KeyGroup | 'person';

const NOTE_GROUPS: readonly WireNoteGroup[] = [...KEY_GROUPS, 'person'];

/** The most bytes a note's body holds at the relay (part 38a's `MAX_ITEM_BYTES`). */
export const MAX_NOTE_BYTES = 16 * 1024;

/** A pull's page (part 38a's `ITEM_PAGE`) and the most notes one acknowledgement names (`MAX_ACK_IDS`). */
export const NOTE_PAGE = 200;
export const MAX_NOTE_ACKS = 500;

/**
 * Part 38e (table PN): a note as the owner's pull hands it (part 38a's `Note`): its order, ID, group, thread (an invite,
 * for `person`), author (an invite; null for a writer device's), key epoch, times and sealed body, or null once deleted.
 */
export interface PulledNote {
  readonly seq: number;
  readonly id: Uint8Array<ArrayBuffer>;
  readonly group: WireNoteGroup;
  readonly thread: Uint8Array<ArrayBuffer> | null;
  readonly author: Uint8Array<ArrayBuffer> | null;
  readonly epoch: number;
  readonly at: number;
  readonly body: Uint8Array<ArrayBuffer> | null;
}

/** A page of the pull: the share's end, its notes, and whether more follow. */
export interface PulledNotes {
  readonly endedAt: number | null;
  readonly notes: readonly PulledNote[];
  readonly more: boolean;
}

/** An ID of {@link INVITE_WIRE_ID_BYTES} bytes or null; undefined when it is neither. */
function idBytesOrNull(value: unknown): Uint8Array<ArrayBuffer> | null | undefined {
  return value === null ? null : (bytesOfLength(value, INVITE_WIRE_ID_BYTES) ?? undefined);
}

/** An epoch as the relay's columns hold it: a whole number below 2^31. */
function epochOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value < 2 ** 31 ? value : null;
}

/** A note's sealed body: base64url of 1 to {@link MAX_NOTE_BYTES} bytes, or null (a tombstone); undefined otherwise. */
function noteBodyOf(value: unknown): Uint8Array<ArrayBuffer> | null | undefined {
  if (value === null) return null;
  if (typeof value !== 'string') return undefined;
  const bytes = fromBase64Url(value);
  return bytes === null || bytes.length === 0 || bytes.length > MAX_NOTE_BYTES ? undefined : bytes;
}

/** A note of a pull's page (the owner's and, part 38c, the invitee's), read field by field; null otherwise. */
export function pulledNoteOf(value: unknown): PulledNote | null {
  const fields = fieldsOf(value);
  const seq = timeOf(fields?.['seq']);
  const id = bytesOfLength(fields?.['id'], INVITE_WIRE_ID_BYTES);
  const group = NOTE_GROUPS.find((known) => known === fields?.['group']) ?? null;
  const thread = idBytesOrNull(fields?.['thread']);
  const author = idBytesOrNull(fields?.['author']);
  const epoch = epochOf(fields?.['epoch']);
  const at = timeOf(fields?.['at']);
  const changedAt = timeOf(fields?.['changedAt']);
  const body = noteBodyOf(fields?.['body']);
  if (seq === null || id === null || group === null || thread === undefined || author === undefined || epoch === null || at === null || changedAt === null || body === undefined) return null;
  return { seq, id, group, thread, author, epoch, at, body };
}

/** `POST /v1/shares/pull`: `{ endedAt, notes, more }`, or null. */
export function readPulledNotes(value: unknown): PulledNotes | null {
  const fields = fieldsOf(value);
  const endedAt = timeOrNull(fields?.['endedAt']);
  const raw = fields?.['notes'];
  const more = fields?.['more'];
  if (endedAt === undefined || !Array.isArray(raw) || raw.length > NOTE_PAGE || typeof more !== 'boolean') return null;
  const notes = raw.map(pulledNoteOf);
  return notes.every((note): note is PulledNote => note !== null) ? { endedAt, notes, more } : null;
}

/** `POST /v1/shares/note`: `{ seq }`, or null. */
export function readNoteSeq(value: unknown): number | null {
  return timeOf(fieldsOf(value)?.['seq']);
}

/** `POST /v1/shares/ack`: `{ acknowledged }`, or null. */
export function readNotesAcknowledged(value: unknown): number | null {
  return timeOf(fieldsOf(value)?.['acknowledged']);
}

/** `POST /v1/shares/role-keys`'s body (DN2): one group's key at `epoch`, wrapped for each invite named (base64url IDs). */
export function roleKeysBody(share: string, owner: Uint8Array, group: KeyGroup, epoch: number, wraps: ReadonlyMap<string, Uint8Array>): Record<string, unknown> {
  return shareBody(share, owner, { group, epoch, wraps: Object.fromEntries([...wraps].map(([invite, wrap]) => [invite, toBase64Url(wrap)])) });
}

/** `POST /v1/shares/note`'s body (table WN): the note's ID, group, epoch, sealed body and tag, and its thread for `person`. */
export function writerNoteBody(share: string, owner: Uint8Array, note: Uint8Array, group: WireNoteGroup, epoch: number, body: Uint8Array, thread: Uint8Array | null): Record<string, unknown> {
  return shareBody(share, owner, { id: toBase64Url(note), group, epoch, body: toBase64Url(body), thread: thread === null ? null : toBase64Url(thread) });
}
